import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import useAuth from 'src/Hooks/Authentication/useAuth';
import { getBrands, getCurrentAgency, getCurrentBrand } from 'src/Redux/Perimeter/Selectors';
import { perimeterActions } from 'src/Redux/Perimeter/Slice';
import { PerimeterState } from 'src/Redux/Perimeter/Types';
import { ConsultantPerimeter, ConsultantsService } from 'src/Services/API';
import { isValidActivePerimeter } from 'src/Services/Perimeter';

import { useModalWithLocation } from '../Navigation';
import { QueryKeys } from '../types';

export const useFetchPerimeter = (
  extraOptions: UseQueryOptions<ConsultantPerimeter, unknown, ConsultantPerimeter, QueryKeys[]>
) => {
  const { isAuthenticated } = useAuth();

  return useQuery(
    [QueryKeys.fetchPerimeter],
    () => {
      return ConsultantsService.consultantsControllerGetPerimeter();
    },
    {
      enabled: isAuthenticated,
      refetchOnWindowFocus: false,
      ...extraOptions,
    }
  );
};

export const useCheckActivePerimeter = () => {
  const dispatch = useDispatch();
  const currentBrand = useSelector(getCurrentBrand);
  const currentAgency = useSelector(getCurrentAgency);
  const brands = useSelector(getBrands);
  const { open } = useModalWithLocation('perimeter-selection');
  const { location } = useHistory();
  const setSelectedAgency = useCallback(
    (selectedAgency?: string) => dispatch(perimeterActions.setCurrentAgency(selectedAgency)),
    [dispatch]
  );
  const setSelectedBrand = useCallback(
    (selectedBrand?: PerimeterState['currentBrand']) =>
      dispatch(perimeterActions.setCurrentBrand(selectedBrand)),
    [dispatch]
  );
  const params = new URLSearchParams(window.location.search);

  useFetchPerimeter({
    onSuccess: perimeter => {
      const brand = brands?.find(company => company.brandCodeApiHeader === params.get('brand'));

      if (params.get('agency') && brand) {
        setSelectedBrand(brand);
        setSelectedAgency(params.get('agency') ?? '');
      } else if (
        location.pathname !== '/perimeter-selection' &&
        !isValidActivePerimeter({ currentBrand, currentAgency, perimeter })
      ) {
        dispatch(perimeterActions.resetCurrent());
        dispatch(perimeterActions.setPerimeter(perimeter));
        open({});
      }
    },
  });
};
